import type { UserSessionQuery } from './__generated__/graphql';
import type { Authenticated, AuthenticationError, AuthenticationState } from './AuthContext';
import type { GraphqlClientResponse } from './graphql/client';

export type TrackError = (
  label: string,
  error?: Error,
  data?: {
    [k: string]: unknown;
  },
) => void;

function convertHumanToUser(
  human: NonNullable<NonNullable<UserSessionQuery['web']['userSession']>['human']>,
  trackError: TrackError | undefined,
): Authenticated | AuthenticationError {
  // TODO: Remove this check (see feature request here: https://github.com/opendoor-labs/code/pull/35285)
  if (!human.email) {
    const message = 'invalid data returned: missing Human.email';
    trackError && trackError(message);
    return {
      state: 'error',
      user: undefined,
      message,
    };
  }

  const fullName = human.full_name || human.first_name || 'Customer';
  const firstName = human.first_name || human.full_name || 'Customer';

  const hasOpendoorEmail = human.email.endsWith('@opendoor.com');
  const hasOpendoorInternalAccess = human.opendoor_internal_access || false;
  const impersonated = human.impersonated || false;

  return {
    state: 'authenticated',
    user: {
      humanToken: human.token,
      customerUuid: human.customer_uuid || null,
      email: human.email,
      fullName,
      firstName,
      hasOpendoorEmail,
      hasOpendoorInternalAccess,
      primaryPhone: human.primary_phone || null,
      impersonated,
    },
  };
}

export const parseAuthentication = (
  queryResult: {
    data: GraphqlClientResponse<UserSessionQuery>['data'];
    error?: GraphqlClientResponse<UserSessionQuery>['error'];
    loading: boolean;
  },
  trackError: TrackError | undefined,
): AuthenticationState => {
  if (queryResult.loading) {
    return {
      state: 'authenticating',
      user: undefined,
    };
  }
  if (queryResult.error) {
    // The error originates in the consumer's transport; its message and
    // enumerable fields can embed the userSession response body (session token,
    // email, customer uuid). Log a stable label with only the error's class name
    // — never its payload — and keep the dev-only state message stable. See
    // src/graphql/AGENTS.md.
    trackError &&
      trackError('UserSession error', new Error('UserSession request failed'), {
        errorName: queryResult.error.name,
      });
    return {
      state: 'error',
      user: undefined,
      message: 'UserSession request failed',
    };
  }
  const userSession = queryResult.data?.web.userSession;
  if (!userSession) {
    // If UserSession is missing, the server returned invalid data.
    const message = 'invalid data returned: missing UserSession';
    trackError && trackError(message);
    return {
      state: 'error',
      user: undefined,
      message,
    };
  }

  if (!userSession.human) {
    return {
      state: 'not_authenticated',
      user: null,
    };
  }

  return convertHumanToUser(userSession.human, trackError);
};
